"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { appConfig } from "@/config/app.config";
import { Icon } from "@iconify/react";
import { cn } from "@/utils/cn";
import "@/styles/page-section/_hero-section.scss";

const DEFAULT_FALLBACK = {
  image: "/assets/images/hero_bg.png",
  mobileImage: null as string | null,
  alt: "Banner",
};

/** Kiểu nút CTA — class `hero-section__btn--outline` / `--ghost` */
export type HeroButtonVariant = "primary" | "outline" | "ghost";

function normHeroButtonVariant(
  v: string | undefined | null,
): HeroButtonVariant {
  if (v === "outline" || v === "ghost") return v;
  return "primary";
}

export type SlideItem = {
  image: string;
  mobileImage?: string | null;
  alt?: string | null;
  /** Ghi đè nội dung overlay; để trống dùng overlayDefaults */
  brand?: string;
  title?: string;
  button?: {
    label?: string;
    href?: string;
    variant?: HeroButtonVariant;
    /** Mở tab mới (ưu tiên hơn mặc định khi được chỉ định) */
    openInNewTab?: boolean;
  };
  /** Mặc định true */
  showContent?: boolean;
  /** Mặc định true — lớp phủ gradient */
  showOverlay?: boolean;
};

/** Giá trị mặc định cho overlay (phần Hero CMS) */
export type HeroOverlayDefaults = {
  brand: string;
  title: string;
  button: {
    label: string;
    href: string;
    variant?: HeroButtonVariant;
    openInNewTab?: boolean;
  };
};

function mergeHeroSlideOverlay(
  slide: SlideItem,
  defaults: HeroOverlayDefaults,
) {
  const showContent = slide.showContent !== false;
  const showOverlay = slide.showOverlay !== false;
  const brand = (slide.brand?.trim() || defaults.brand).trim() || defaults.brand;
  const title = (slide.title?.trim() || defaults.title).trim() || defaults.title;
  const label =
    (slide.button?.label?.trim() || defaults.button.label).trim() ||
    defaults.button.label;
  const href =
    (slide.button?.href?.trim() || defaults.button.href).trim() ||
    defaults.button.href;
  const variant = normHeroButtonVariant(
    slide.button?.variant ?? defaults.button.variant,
  );
  const slideTab = slide.button?.openInNewTab;
  const openInNewTab =
    slideTab === true ||
    (slideTab !== false && defaults.button.openInNewTab === true);
  return {
    showContent,
    showOverlay,
    brand,
    title,
    button: { label, href, variant, openInNewTab },
  };
}

/** Một mục breadcrumb: href có = link, không có = mục hiện tại (active) */
export type BreadcrumbItem = {
  label: string;
  href?: string;
};

export interface HeroSectionProps {
  /** pageKey trùng với admin/API (home, gioi-thieu, dai-hoc, ...) */
  pageKey: string;
  /** carousel = nhiều slide có nút prev/next; single = chỉ 1 ảnh */
  variant?: "carousel" | "single";
  className?: string;
  ariaLabel?: string;
  /** Modifier CSS cho section (vd: hero-section--banner hero-section--about) */
  sectionModifier?: string;
  /** Override slide (ưu tiên hơn fallback mặc định) */
  overrideSlide?: SlideItem | null;
  /** Override multiple slides */
  overrideSlides?: SlideItem[] | null;
  /** Nội dung overlay (vd. title, breadcrumb) — render trong cùng section sau __bg */
  children?: React.ReactNode;
  /** Breadcrumb đồng bộ: [{ label, href? }, ...]. Cuối cùng không href = mục hiện tại. */
  breadcrumb?: BreadcrumbItem[];
  /** Ẩn ảnh nền hero, chỉ giữ overlay/breadcrumb nếu cần */
  hideBackgroundImage?: boolean;
  /**
   * Khi có: render overlay brand/title/nút theo từng slide (đồng bộ carousel).
   * Khi không: dùng `children` như cũ.
   */
  overlayDefaults?: HeroOverlayDefaults | null;
}

export function HeroSection({
  pageKey,
  variant = "single",
  className = "",
  ariaLabel = "Banner",
  sectionModifier = "",
  overrideSlide = null,
  overrideSlides = null,
  children,
  breadcrumb,
  hideBackgroundImage = false,
  overlayDefaults = null,
}: HeroSectionProps) {
  const heroPageKey = pageKey.trim() || "home";
  const hasOverrideSlides = Array.isArray(overrideSlides) && overrideSlides.length > 0;
  const overrideImage =
    !hasOverrideSlides && overrideSlide && typeof overrideSlide.image === "string"
      ? overrideSlide.image.trim()
      : ""

  const override =
    overrideImage !== ""
      ? {
        image: overrideImage,
        mobileImage: overrideSlide?.mobileImage ?? null,
        alt: overrideSlide?.alt ?? "Banner",
      }
      : null;

  const [slides] = useState<SlideItem[]>(() => {
    if (typeof process === "undefined") return [];
    if (!appConfig.api.publicUrl?.trim()) return [DEFAULT_FALLBACK];
    return [DEFAULT_FALLBACK];
  });
  const [currentSlide, setCurrentSlide] = useState(0);

  const displaySlides = hasOverrideSlides ? overrideSlides! : override ? [override] : slides.length > 0 ? slides : [DEFAULT_FALLBACK];

  const maxSlideIdx = Math.max(0, displaySlides.length - 1);
  const slideIndex = Math.min(Math.max(0, currentSlide), maxSlideIdx);

  /**
   * Ảnh nền có thể bấm chỉ khi bật "mở tab mới" — khi tắt thì không dùng URL chuyển trang.
   */
  const wrapSlidePictureIfLinked = (
    slide: SlideItem,
    picture: React.ReactNode,
  ) => {
    if (!overlayDefaults) return picture;
    const eff = mergeHeroSlideOverlay(slide, overlayDefaults);
    if (!eff.button.openInNewTab) return picture;
    const href = (eff.button.href || "#about").trim() || "#about";
    const labelHint =
      eff.button.label?.trim() || eff.title?.trim() || "Banner";
    return (
      <a
        href={href}
        className="hero-section__slide-link"
        aria-label={`Mở liên kết — ${labelHint}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {picture}
      </a>
    );
  };

  const renderManagedOverlay = (slide: SlideItem) => {
    if (!overlayDefaults) return null;
    const eff = mergeHeroSlideOverlay(slide, overlayDefaults);
    if (!eff.showContent && !eff.showOverlay) return null;
    return (
      <div
        className={cn(
          "hero-section__overlay d-sm-none",
          !eff.showOverlay && "hero-section__overlay--no-scrim",
        )}
      >
        <div className="container">
          {eff.showContent ? (
            <div className="hero-section__content">
              <p className="hero-section__brand">{eff.brand}</p>
              <h1
                className="hero-section__title"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {eff.title}
              </h1>
              {eff.button.openInNewTab ? (
                <a
                  href={(eff.button.href || "#about").trim() || "#about"}
                  className={cn(
                    "btn hero-section__btn",
                    eff.button.variant !== "primary" &&
                      `hero-section__btn--${eff.button.variant}`,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{eff.button.label}</span>
                  <span
                    className="hero-section__btn-icon"
                    aria-hidden="true"
                  ></span>
                </a>
              ) : (
                <span
                  className={cn(
                    "btn hero-section__btn",
                    eff.button.variant !== "primary" &&
                      `hero-section__btn--${eff.button.variant}`,
                  )}
                >
                  <span>{eff.button.label}</span>
                  <span
                    className="hero-section__btn-icon"
                    aria-hidden="true"
                  ></span>
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const overlayNode = overlayDefaults
    ? renderManagedOverlay(displaySlides[slideIndex] ?? displaySlides[0])
    : children;
  const showCarousel = variant === "carousel" && displaySlides.length > 1;
  const first = displaySlides[0];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const max = Math.max(0, displaySlides.length - 1);
      const i = Math.min(Math.max(0, prev), max);
      return i >= max ? 0 : i + 1;
    });
  }, [displaySlides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => {
      const max = Math.max(0, displaySlides.length - 1);
      const i = Math.min(Math.max(0, prev), max);
      return i <= 0 ? max : i - 1;
    });
  };

  useEffect(() => {
    if (!showCarousel || displaySlides.length <= 1) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [showCarousel, displaySlides.length, nextSlide]);

  const sectionClass = [
    "hero-section",
    sectionModifier,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const breadcrumbNav =
    breadcrumb && breadcrumb.length > 0 ? (
      <nav className="hero-breadcrumb" aria-label="Breadcrumb">
        <div className="hero-breadcrumb__inner">
          <ol className="hero-breadcrumb__list">
            {breadcrumb.map((item, i) => {
              const isLast = i === breadcrumb.length - 1;
              const isActive = isLast || !item.href;
              return (
                <li
                  key={i}
                  className={`hero-breadcrumb__item${isActive ? " hero-breadcrumb__item--active" : ""}`}
                >
                  {item.href != null && item.href !== "" ? (
                    <Link href={item.href} className="hero-breadcrumb__link">
                      {item.label}
                    </Link>
                  ) : (
                    <span className="hero-breadcrumb__current">{item.label}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    ) : null;

  if (showCarousel) {
    if (hideBackgroundImage) {
      return breadcrumbNav ? <>{breadcrumbNav}</> : null;
    }
    return (
      <>
        <section
          className={sectionClass}
          aria-label={ariaLabel}
          data-page-key={heroPageKey}
        >
          <div className="hero-section__bg">
            {displaySlides.map((slide, index) => (
              <div
                key={index}
                className={`hero-section__slide ${index === slideIndex ? "active" : ""}`}
              >
                {wrapSlidePictureIfLinked(
                  slide,
                  <picture>
                    {slide.mobileImage && (
                      <source
                        media="(max-width: 768px)"
                        srcSet={slide.mobileImage}
                      />
                    )}
                    <img
                      src={slide.image}
                      alt={slide.alt ?? "Banner"}
                      className="hero-section__image"
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  </picture>,
                )}
              </div>
            ))}
            {overlayNode}
            <button
              className="carousel-control-prev"
              type="button"
              onClick={prevSlide}
              aria-label="Slide trước"
              suppressHydrationWarning
            >
              <Icon
                icon="mdi:chevron-left"
                width={32}
                height={32}
                color="white"
              />
            </button>
            <button
              className="carousel-control-next"
              type="button"
              onClick={nextSlide}
              aria-label="Slide kế tiếp"
              suppressHydrationWarning
            >
              <Icon
                icon="mdi:chevron-right"
                width={32}
                height={32}
                color="white"
              />
            </button>
          </div>
        </section>
        {breadcrumbNav}
      </>
    );
  }

  if (hideBackgroundImage) {
    return breadcrumbNav ? <>{breadcrumbNav}</> : null;
  }

  return (
    <>
      <section
        className={sectionClass}
        aria-label={ariaLabel}
        data-page-key={heroPageKey}
      >
        {!hideBackgroundImage && (
          <div className="hero-section__bg">
            <div className="hero-section__slide active">
              {wrapSlidePictureIfLinked(
                first,
                <picture>
                  {first.mobileImage && (
                    <source media="(max-width: 768px)" srcSet={first.mobileImage} />
                  )}
                  <img
                    src={first.image}
                    alt={first.alt ?? "Banner"}
                    className="hero-section__image"
                    loading="eager"
                  />
                </picture>,
              )}
            </div>
          </div>
        )}
        {overlayNode}
      </section>
      {breadcrumbNav}
    </>
  );
}

export default HeroSection;

